import PropTypes from 'prop-types';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import {
  ticketParticipantsSet,
  ticketSet,
  ticketStagesSet,
} from '~/actions/ticket';
import {
  addNewReminder,
  checkESignatureLogin,
  getTicketParticipants,
  patchTicketApproval,
  signDocument,
} from '~/api/tickets';
import AccessControl from '~/components/Shared/AccessControl/AccessControl';
import { showToast } from '~/components/Shared/EcToast/showToast';
import SendReminder from '~/components/Shared/SendReminder/SendReminder';
import { Button } from '~/eds/src/components/Button/Button';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { TicketNegotiationStatus } from '~/enums/TicketNegotiationStatus';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { withUsers } from '~/hocs/withUsers';
import { useAsync } from '~/hooks/useAsync';
import { getJudgmentById } from '~/reducers/ticketReviewer';
import { api } from '~/redux/api/createApi';
import * as toastTypes from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Text from '~/ui/components/Text/Text';
import { testIsCounterpartySigner, testIsUnassignedUser } from '~/utils/ticket';

import TicketUserItem from '../TicketUserItem/TicketUserItem';
import ApprovalItemApproveReject from './ApprovalItemApproveReject';
import styles from './TicketApprovalItem.styles';
import { getStatusLabel } from './utils';

const reminderPermission = {
  [TicketStageType.Review]: TicketPermissionType.RemindReviewers,
  [TicketStageType.Sign]: TicketPermissionType.RemindSignatories,
  [TicketStageType.Finalize]: TicketPermissionType.RemindFinalizers,
};

const TicketApprovalItem = ({
  issueId,
  entityType,
  approvalList,
  approvalName,
  // injected props
  classes,
  users,
  // connected props
  approvalItem,
  currentUserId,
  isCompleted,
  ticket,
  ticketParticipantsSet,
  ticketStagesSet,
}) => {
  const approvalId = approvalItem.id;
  const ticketId = ticket.id;
  const [eSignatureInfo, setESignatureInfo] = useState({ error: true });
  const intl = useIntl();
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);
  const [getTicketCurrentStage] =
    api.endpoints.getTicketCurrentStage.useLazyQuery();

  useAsync(
    checkESignatureLogin,
    {
      ticketId,
      esignatureProvider: ticket.esignatureProvider,
      ticketUrl: window.location.href,
    },
    {
      condition: !!ticket.esignatureProvider && !isEsignatureV2RevampEnabled,
      deps: [ticketId],
      successHandler: setESignatureInfo,
      errorToastMessage: intl.formatMessage({
        id: 'TicketApprovalItem.eSignatureCommunicationError',
        defaultMessage:
          'There was an issue communicating with the e-signature provider. Please try again.',
        description:
          'Error message when communication with e-signature provider fails.',
      }),
    },
  );

  const { executor: sendNewReminder } = useAsync(
    addNewReminder,
    { ticketId, approvalId },
    {
      successHandler: async () => {
        const stagesResp = await getTicketCurrentStage({ ticketId }).unwrap();
        ticketStagesSet(stagesResp);
      },
      successToastMessage: intl.formatMessage({
        id: 'TicketApprovalItem.reminderSentSuccess',
        defaultMessage: 'Reminder sent successfully.',
        description: 'Success toast message when a reminder is sent.',
      }),
      errorToastMessage: intl.formatMessage({
        id: 'TicketApprovalItem.reminderSentError',
        defaultMessage: 'Sending reminder failed.',
        description: 'Error toast message when sending a reminder fails.',
      }),
    },
  );

  const { executor: handleOnMarkAsDoneClick, isLoading: isMarkDoneLoading } =
    useAsync(
      patchTicketApproval,
      { ticketId, status: TicketStatusType.Done, approvalId },
      {
        successHandler: async () => {
          const stagesResp = await getTicketCurrentStage({ ticketId }).unwrap();
          const { data: participants } = await getTicketParticipants({
            ticketId,
          });
          ticketParticipantsSet(participants);
          ticketStagesSet(stagesResp);
        },
      },
    );

  const isSentToSupplierPortal =
    ticket.negotiationStatus === TicketNegotiationStatus.InProgress;

  const handleOnSignClick = () => {
    if (eSignatureInfo.isUserEsignatureLoggedIn) {
      signDocument({ ticketId: ticket.id, ticketUrl: window.location.href })
        .then(({ redirectUrl }) => window.open(redirectUrl, '_blank'))
        .catch(() =>
          showToast(
            toastTypes.ERROR,
            intl.formatMessage({
              id: 'TicketApprovalItem.signDocumentError',
              defaultMessage:
                'Unable to sign the document. Please refresh the page and try again.',
              description: 'Error message when signing a document fails.',
            }),
          ),
        );
    } else {
      const esignatureAuthenticateUrl = eSignatureInfo.redirectUrl;
      window.open(esignatureAuthenticateUrl, '_blank');
    }
  };

  const renderApprovalItemActions = () => {
    switch (ticket.stage) {
      case TicketStageType.Finalize:
        return (
          <FlexLayout alignItems="center">
            <Button
              text={intl.formatMessage({
                id: 'TicketApprovalItem.markAsDone',
                defaultMessage: 'Mark as Done',
                description: 'Button text to mark an item as done.',
              })}
              isLoading={isMarkDoneLoading}
              disabled={isSentToSupplierPortal}
              onClick={handleOnMarkAsDoneClick}
            />
          </FlexLayout>
        );
      case TicketStageType.Sign:
        return (
          <FlexLayout alignItems="center">
            {ticket.hasEnvelope &&
              approvalItem.status === TicketStatusType.SignaturePending && (
                <Button
                  text={intl.formatMessage({
                    id: 'TicketApprovalItem.signDocument',
                    defaultMessage: 'Sign Document',
                    description: 'Button text to sign a document.',
                  })}
                  disabled={isSentToSupplierPortal}
                  onClick={handleOnSignClick}
                />
              )}
            <span className={classes.horizontalSeparator} />
            {renderSingleStatus(
              approvalItem.status,
              <Icon icon="donut" color="yellow-500" size="s" />,
            )}
          </FlexLayout>
        );
      case TicketStageType.Review:
        return (
          <ApprovalItemApproveReject
            ticketId={ticket.id}
            approvalName={approvalName}
            approvalId={approvalId}
            phaseId={approvalItem.phaseId}
            disabled={isSentToSupplierPortal}
          />
        );
      default:
        return null;
    }
  };

  const renderSingleStatus = (label, icon, datetime) => {
    const statusLabel = getStatusLabel(label, intl);
    return (
      <FlexLayout
        className={classes.approvalItemStatus}
        alignItems="center"
        space={1}
      >
        {icon}
        <Text color="gray-700" variant="xs-dense">
          {statusLabel}{' '}
          {datetime && (
            <DateText
              date={datetime ? new Date(datetime) : undefined}
              format="duration"
              enableTick
            />
          )}
        </Text>
      </FlexLayout>
    );
  };

  const [assignAction, setAssignAction] = useState(false);
  const handleReassignment = () => setAssignAction(true);
  const judgment = getJudgmentById(ticket, issueId);

  /**
   * Counterparty signers only exist in Sign stage. If the signature request has already been sent
   * and the userId is null, that means the signer has been reassigned to an external user in DocuSign.
   * In Review/Finalize stages, null userId indicates a deleted department user, not a counterparty.
   */
  const isCounterpartySigner =
    ticket.stage === TicketStageType.Sign &&
    (approvalItem.status === TicketStatusType.SignatureRequestNotSent
      ? testIsCounterpartySigner(judgment)
      : approvalItem.userId === null);
  const isUnassignedUser = testIsUnassignedUser(approvalItem, users);

  let itemStatus;
  switch (approvalItem.status) {
    case TicketStatusType.Done:
    case TicketStatusType.Approved:
      itemStatus = (
        <FlexLayout alignItems="center">
          {renderSingleStatus(
            TicketStatusType.Completed,
            <Icon icon="checkmarkCircledFilled" color="green-500" size="s" />,
            approvalItem.lastModified,
          )}
        </FlexLayout>
      );
      break;
    case TicketStatusType.Signed:
      itemStatus = (
        <FlexLayout alignItems="center">
          {renderSingleStatus(
            approvalItem.signatureUploadDate
              ? `${approvalItem.status} via Upload`
              : approvalItem.status,
            <Icon icon="checkmarkCircledFilled" color="green-500" size="s" />,
            // TODO: re-enable this after we finish e-sig 2.0
            // approvalItem.lastModified,
          )}
        </FlexLayout>
      );
      break;
    case TicketStatusType.Rejected:
      itemStatus = (
        <FlexLayout alignItems="center">
          {renderSingleStatus(
            'Rejected',
            <Icon icon="stop" color="red-600" size="s" />,
            approvalItem.lastModified,
          )}
        </FlexLayout>
      );
      break;
    case TicketStatusType.EmailDeliveryFailed:
      itemStatus = (
        <FlexLayout alignItems="center">
          {renderSingleStatus(
            approvalItem.status,
            <Icon icon="warning" color="red-600" size="s" />,
            approvalItem.lastModified,
          )}
        </FlexLayout>
      );
      break;
    default:
      if (!isCounterpartySigner && isUnassignedUser) {
        itemStatus = (
          <>
            <AccessControl
              userPermissions={ticket.permissions}
              requiredPermission={TicketPermissionType.ReassignAny}
            >
              <Button
                text={intl.formatMessage({
                  id: 'TicketApprovalItem.assignButton',
                  defaultMessage: 'Assign',
                  description: 'Button text to assign a user.',
                })}
                label={intl.formatMessage({
                  id: 'TicketApprovalItem.assignLabel',
                  defaultMessage: 'Assign',
                  description: 'Label for the assign button.',
                })}
                onClick={handleReassignment}
                variant="action"
              />
            </AccessControl>

            {renderSingleStatus(
              approvalItem.status,
              <Icon icon="donut" color="red-400" size="s" />,
            )}
          </>
        );
      } else if (
        approvalItem.userId === currentUserId ||
        (ticket.permissions.includes(TicketPermissionType.FinalizeItem) &&
          ticket.stage === TicketStageType.Finalize)
      ) {
        itemStatus = renderApprovalItemActions();
      } else {
        itemStatus = (
          <>
            <AccessControl
              userPermissions={ticket.permissions}
              requiredPermission={reminderPermission[ticket.stage]}
            >
              <FlexLayout alignItems="center">
                {(ticket.stage !== TicketStageType.Sign ||
                  ticket.stages.sign.status !==
                    TicketStatusType.SignatureRequestNotSent) && (
                  <SendReminder
                    actionHandler={sendNewReminder}
                    lastReminderDate={
                      approvalItem.lastReminderDate || undefined
                    }
                  />
                )}
              </FlexLayout>
            </AccessControl>
            {renderSingleStatus(
              approvalItem.status,
              <Icon
                icon="donut"
                color={isUnassignedUser ? 'red-400' : 'yellow-500'}
                size="s"
              />,
            )}
          </>
        );
      }
  }

  const { userId: approverId, status } = approvalItem;

  const note = (status === TicketStatusType.Approved ||
    status === TicketStatusType.Rejected) && (
    <div className={classes.note}>
      {approvalItem.comment || (
        <i>
          {intl.formatMessage({
            id: 'TicketApprovalItem.noNoteProvided',
            defaultMessage: 'No note provided.',
            description: 'Text to display when no note is provided.',
          })}
        </i>
      )}
    </div>
  );

  return (
    <div className={classes.ticketApprovalItemWrapper}>
      <TicketUserItem
        userId={approverId}
        externalUser={approvalItem.externalUser}
        currentUserId={currentUserId}
        userPermissions={ticket.permissions}
        isListItem={true}
        note={note}
        ticketId={ticketId}
        issueId={issueId}
        entityType={entityType}
        approvalList={approvalList}
        status={status}
        disableAdditionalActions={
          isCompleted ||
          [TicketStatusType.Signed].includes(status) ||
          approvalItem.externalUser
        }
        assignAction={assignAction}
        setAssignAction={setAssignAction}
        isCounterpartySigner={isCounterpartySigner}
      >
        {itemStatus}
      </TicketUserItem>
    </div>
  );
};

export const propTypes = {
  approvalId: PropTypes.string.isRequired,
};

TicketApprovalItem.propTypes = propTypes;

function mapStateToProps({ currentUser, ticket }, { approvalId }) {
  const { approvals } = ticket;
  return {
    currentUserId: currentUser.id,
    approvalItem: approvals[approvalId],
    isCompleted: ticket.status === TicketStatusType.Completed,
    ticket,
  };
}

export default injectSheet(styles)(
  connect(mapStateToProps, {
    ticketSet,
    ticketParticipantsSet,
    ticketStagesSet,
  })(withUsers(TicketApprovalItem)),
);
